import { Trans } from '@lingui/react';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import {
  GqlDocSystemTagKind,
  GqlMeeting,
  GqlShareIssue,
  GqlShareIssueParticipant,
  GqlShareIssueRegistration,
  type GqlMeetingConcern,
} from '../../../../graphql/base-types.graphql';
import { DisplayDocument, Document } from '@startuptools/business-ui/document/Document';
import { DocGenRelationName } from '@startuptools/common/doc-gen';
import { apiClient } from '../../../../common/http';
import { useApolloClient } from '@apollo/client';
import { ShareIssueDocument } from '../../ShareIssue.graphql';
import { BoardMembersTable } from '../../../common/components/BoardMembersTable';
import {
  useCreateShareIssueBoardMemberMutation,
  useDeleteShareIssueBoardMemberMutation,
} from './StepBoardStatement.graphql';
import { Divider } from '@startuptools/ui/Divider';
import { PaymentKind } from '@startuptools/common/participants';
import { Alert } from '@startuptools/ui/Alert';
import { ParticipantsTable } from '../ParticipantsTable';
import { DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../components/steppers/common/StepperStepContentContext';
import { ErrorDisplay } from '../../../../components/error-display/ErrorDisplay';

const getDefaultFilename = (mc: Pick<GqlMeetingConcern, 'appendixNumber'> | null | undefined) => {
  const appendixName = `${mc?.appendixNumber ?? 'x'}.3`;
  return `Appendix ${appendixName} - Revisorns yttrande angående kvittning.pdf`;
};

export const SiStepPayByOffset = ({
  shareIssue,
  shareIssueRegistration,
}: {
  shareIssue: Pick<
    GqlShareIssue,
    | 'id'
    | 'companyId'
    | 'nonCashStatementPl'
    | 'nonCashStatementSl'
    | 'paymentKindOffset'
    | 'paymentKindNonCash'
    | 'paymentKindCash'
    | 'pricePerShare'
    | 'currency'
    | 'companyId'
    | 'maxNumberOfShares'
    | 'shareClasses'
    | 'secondaryLang'
  > & {
    meetingConcern?:
      | (Pick<GqlMeetingConcern, 'id' | 'appendixNumber'> & { meeting: Pick<GqlMeeting, 'id' | 'startTime' | 'kind'> })
      | null;
    boardMembers: { id: string; identity: DisplayIdentity }[];
    latestAuditorStatementOffset?: DisplayDocument | null;
  };
  shareIssueRegistration: Pick<GqlShareIssueRegistration, 'id'> & {
    participants: (Pick<
      GqlShareIssueParticipant,
      'id' | 'paymentKind' | 'paymentStatus' | 'numberOfShares' | 'shareClass'
    > & {
      identity: DisplayIdentity;
      proxy?: DisplayIdentity | null;
    })[];
  };
}) => {
  const apolloClient = useApolloClient();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  // eslint-disable-next-line sut/loading-mutation
  const [doDeleteBm] = useDeleteShareIssueBoardMemberMutation({
    refetchQueries: [ShareIssueDocument],
    awaitRefetchQueries: true,
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doCreateBm] = useCreateShareIssueBoardMemberMutation({
    refetchQueries: [ShareIssueDocument],
    awaitRefetchQueries: true,
  });

  const defaultFilename = getDefaultFilename(shareIssue.meetingConcern);

  const sips = shareIssueRegistration.participants.filter(sip => sip.paymentKind === PaymentKind.Offset);

  return (
    <Stack gap={3}>
      <Typography level="h4">
        <Trans id="Statement regarding offset payment" />
      </Typography>

      <Alert>
        <Trans id="Since part of the payment is made through offset, the board must provide a statement regarding the offset. Here, you specify the amount each investor is owed by the company and how much of this claim will be used for offset. The offset amount can never exceed the claim. You also need to select which board members will sign the statement. At least half of the board must sign." />
      </Alert>

      <ParticipantsTable
        paymentKind={PaymentKind.Offset}
        shareIssue={shareIssue}
        sips={sips}
        sirId={shareIssueRegistration.id}
      />

      <Divider />

      <Typography level="h4">
        <Trans id="Who is signing?" />
      </Typography>

      <BoardMembersTable
        boardMembers={shareIssue.boardMembers}
        refetchQueries={[ShareIssueDocument]}
        onDelete={id => doDeleteBm({ variables: { companyId: shareIssue.companyId, id } })}
        onCreate={identity =>
          doCreateBm({
            variables: {
              companyId: shareIssue.companyId,
              input: { shareIssueId: shareIssue.id, identityInput: identityToInput(identity) },
            },
          })
        }
      />

      <Divider />

      <Typography level="h4">
        <Trans id="Upload auditor statement for offset payments" />
      </Typography>

      <Document
        defaultFileName={defaultFilename}
        document={shareIssue.latestAuditorStatementOffset}
        upload={async data => {
          const file = data.file;
          const relation = {
            relation_id: shareIssue.id,
            relation_name: DocGenRelationName.ShareIssueId,
          };
          await apiClient.uploadFile(
            file,
            file.name,
            shareIssue.companyId,
            relation,
            false,
            GqlDocSystemTagKind.AuditorsStatementOffset,
          );
          return apolloClient.refetchQueries({
            include: [ShareIssueDocument],
          });
        }}
      />

      {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

      <Box>
        <Button onClick={() => stepNext()}>
          <Trans id="Continue" />
        </Button>
      </Box>
    </Stack>
  );
};
